import { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material';
import { CheckCircleOutline } from '@mui/icons-material';
import { DialogWrapper } from '../../presentation-components/display-elements-components/dialog-wrapper';
import { QuestionInput } from '../../presentation-components/surveys/respond/question-inputs';
import { evaluateRule, isItemAnswered, isItemInRange, isItemRequired } from '../../presentation-components/surveys/respond/respond-utils';
import { ISurvey, ISurveyQuestionGroupItem, ISurveyResponseItem } from '../../../shared/services/survey/survey.model';

interface Props {
  open: boolean;
  onClose: () => void;
  survey: ISurvey;
}

/**
 * Walks an admin through the survey exactly as a recipient meets it: the same
 * question inputs, page navigation, display conditions and required-field
 * validation as the public form, but entirely in local state. Nothing typed
 * here is saved and no response session is created.
 */
export const SurveyPreviewDialog = ({ open, onClose, survey }: Props) => {
  const [answers, setAnswers] = useState<Map<string, ISurveyResponseItem>>(new Map());
  const [currentPage, setCurrentPage] = useState(1);
  const [showValidation, setShowValidation] = useState(false);
  const [validationMessage, setValidationMessage] = useState('');
  const [finished, setFinished] = useState(false);

  // The dialog's scroll container is the wrapper's DialogContent; scrolling the
  // topmost element into view mirrors the public form's scroll-to-top on page change.
  const contentTopRef = useRef<HTMLDivElement | null>(null);
  const scrollToTop = () => {
    contentTopRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  };

  const resetPreview = () => {
    setAnswers(new Map());
    setCurrentPage(1);
    setShowValidation(false);
    setValidationMessage('');
    setFinished(false);
  };

  // resetPreview only calls state setters, which are stable across renders.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (open) resetPreview(); }, [open]);

  // The same ordering, paging and visibility computations as the public form
  // (survey-respond-page.tsx), so the preview cannot drift from the truth.
  const allItems = (survey.surveyQuestionGroup?.surveyQuestionGroupItems ?? [])
    .slice()
    .sort((a, b) =>
      (a.pageNumber ?? 1) !== (b.pageNumber ?? 1)
        ? (a.pageNumber ?? 1) - (b.pageNumber ?? 1)
        : a.sortOrder - b.sortOrder,
    );

  const allPageNumbers = [...new Set(allItems.map(i => i.pageNumber ?? 1))].sort((a, b) => a - b);
  if (allPageNumbers.length === 0) allPageNumbers.push(1);
  const maxPage = allPageNumbers[allPageNumbers.length - 1];

  // A page whose questions are all hidden by conditions (or that has none) is skipped by the
  // pager entirely, so a recipient never lands on "No questions on this page". Recomputed from
  // the live answers: answering differently can reveal or hide later pages.
  const pageHasVisibleItems = (page: number) => allItems.some(item =>
    (item.pageNumber ?? 1) === page && evaluateRule(item.visibilityRule, answers),
  );
  const visiblePageNumbers = allPageNumbers.filter(pageHasVisibleItems);
  const totalVisiblePages = Math.max(visiblePageNumbers.length, 1);
  const displayPageIndex = visiblePageNumbers.includes(currentPage)
    ? visiblePageNumbers.indexOf(currentPage) + 1
    : visiblePageNumbers.filter(p => p < currentPage).length + 1;
  const nextVisiblePage = visiblePageNumbers.find(p => p > currentPage);
  const prevVisiblePage = [...visiblePageNumbers].reverse().find(p => p < currentPage);

  const visibleItems = allItems.filter(item =>
    (item.pageNumber ?? 1) === currentPage && evaluateRule(item.visibilityRule, answers),
  );

  const currentPageRequiredAnswered = visibleItems
    .filter(isItemRequired)
    .every(item => isItemAnswered(item, answers));
  const currentPageInRange = visibleItems.every(item => isItemInRange(item, answers));

  const allVisibleItems = allItems.filter(item => evaluateRule(item.visibilityRule, answers));
  const outstandingItems = allVisibleItems.filter(item =>
    (isItemRequired(item) && !isItemAnswered(item, answers)) || !isItemInRange(item, answers),
  );

  const firstOffendingPage = (items: ISurveyQuestionGroupItem[]): number =>
    items.reduce((min, item) => Math.min(min, item.pageNumber ?? 1), maxPage);

  const handleAnswerChange = (answer: ISurveyResponseItem) => {
    setAnswers(prev => {
      const next = new Map(prev);
      const existing = prev.get(answer.surveyQuestionId);
      next.set(answer.surveyQuestionId, { ...existing, ...answer });
      return next;
    });
  };

  const handleSubmit = () => {
    if (outstandingItems.length > 0) {
      setShowValidation(true);
      const count = outstandingItems.length;
      setValidationMessage(`${count} question${count === 1 ? '' : 's'} still need${count === 1 ? 's' : ''} attention before you can submit.`);
      setCurrentPage(firstOffendingPage(outstandingItems));
      scrollToTop();
      return;
    }
    setFinished(true);
  };

  const isMultiPage = totalVisiblePages > 1;
  const isLastPage = nextVisiblePage === undefined;

  return (
    <DialogWrapper
      open={open}
      onClose={onClose}
      title='Survey preview'
      cancelHandler={onClose}
      submitHandler={onClose}
      isSubmitting={false}
      hideCancelButton
      submitButtonText='Close preview'
      maxWidth='sm'
    >
      <Alert ref={contentTopRef} severity='info' sx={{ mb: 2 }}>
        This is a preview of what a recipient sees. Responses here are not saved.
      </Alert>

      {finished && (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <CheckCircleOutline sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          <Typography variant='h5' sx={{ mb: 1 }}>Thank you!</Typography>
          <Typography color='text.secondary' sx={{ mb: 3 }}>
            Your response has been submitted successfully.
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            This is the confirmation a recipient sees. Nothing was recorded from this preview.
          </Typography>
          <Button variant='outlined' onClick={resetPreview}>Start the preview again</Button>
        </Box>
      )}

      {!finished && allItems.length === 0 && (
        <Typography color='text.secondary' sx={{ py: 2 }}>
          This survey has no questions yet, so recipients would see an empty survey.
        </Typography>
      )}

      {!finished && allItems.length > 0 && (
        <Box>
          <Typography variant='h5' sx={{ mb: 0.5 }}>{survey.name}</Typography>
          {survey.description && (
            <Typography color='text.secondary' sx={{ mb: 3 }}>{survey.description}</Typography>
          )}
          {isMultiPage && (
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant='body2' color='text.secondary'>
                Page {displayPageIndex} of {totalVisiblePages}
              </Typography>
              <Box sx={{ flex: 1, height: 4, bgcolor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
                <Box sx={{ height: '100%', width: `${(displayPageIndex / totalVisiblePages) * 100}%`, bgcolor: 'primary.main', transition: 'width 0.3s ease' }} />
              </Box>
            </Box>
          )}
          <Stack spacing={3}>
            {visibleItems.length === 0 ? (
              <Typography color='text.secondary' sx={{ py: 2 }}>
                No questions on this page.
              </Typography>
            ) : (
              visibleItems.map(item => (
                <Box key={item.id}>
                  <Stack spacing={2}>
                    <QuestionInput
                      item={item}
                      answer={answers.get(item.surveyQuestionId)}
                      onChange={handleAnswerChange}
                      showValidation={showValidation}
                    />
                    {item.surveyQuestion?.collectMoreDetails && (
                      <Stack spacing={1}>
                        <Typography
                          id={`${item.surveyQuestion.id}-preview-more-details-heading`}
                          variant='subtitle2'
                          sx={{ fontWeight: 600 }}
                        >
                          Please provide more details:
                        </Typography>
                        <TextField
                          hiddenLabel
                          multiline
                          minRows={2}
                          value={answers.get(item.surveyQuestionId)?.moreDetailsText ?? ''}
                          onChange={e => {
                            const v = e.target.value;
                            handleAnswerChange({
                              surveyQuestionId: item.surveyQuestion!.id,
                              surveyQuestionGroupItemId: item.id,
                              moreDetailsText: v.trim() ? v : null,
                            });
                          }}
                          fullWidth
                          inputProps={{ 'aria-labelledby': `${item.surveyQuestion.id}-preview-more-details-heading` }}
                        />
                      </Stack>
                    )}
                  </Stack>
                </Box>
              ))
            )}
          </Stack>
          <Box sx={{ mt: 4 }}>
            {validationMessage && (
              <Typography color='error' sx={{ mb: 2 }}>{validationMessage}</Typography>
            )}
            <Stack direction='row' justifyContent='space-between' alignItems='center'>
              {isMultiPage && prevVisiblePage !== undefined ? (
                <Button variant='outlined' onClick={() => { setCurrentPage(prevVisiblePage); setValidationMessage(''); }}>
                  ← Back
                </Button>
              ) : <span />}
              {isLastPage ? (
                <Button variant='contained' size='large' onClick={handleSubmit}>
                  Submit
                </Button>
              ) : (
                <Button
                  variant='contained'
                  size='large'
                  disabled={!currentPageRequiredAnswered || !currentPageInRange}
                  onClick={() => { setCurrentPage(nextVisiblePage!); setValidationMessage(''); scrollToTop(); }}
                >
                  Next →
                </Button>
              )}
            </Stack>
          </Box>
        </Box>
      )}
    </DialogWrapper>
  );
};
